import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowUpRight, MessageSquare, MessagesSquare } from "lucide-react";
import React from "react";

export type ChannelIntegration = {
  type: string;
  name: string;
  description: string;
  icon: React.ElementType;
  /** Nº de canais configurados deste tipo (quando aplicável). */
  count?: number;
};

interface ChannelIntegrationCardsProps {
  /** Integrações disponíveis para configurar. */
  integrations?: ChannelIntegration[];
  onConfigure: (type: string) => void;
}

const DEFAULT_INTEGRATIONS: ChannelIntegration[] = [
  {
    type: "whatsapp",
    name: "WhatsApp",
    description: "Atenda seus clientes pela WhatsApp Cloud API.",
    icon: MessagesSquare,
  },
  {
    type: "slack",
    name: "Slack",
    description: "Conecte canais do Slack ao atendimento.",
    icon: MessageSquare,
  },
];

/** Cada integração tem uma cor de marca própria — reforça reconhecimento visual em vez de tudo cair no azul padrão. */
const INTEGRATION_COLORS: Record<string, { icon: string; bg: string }> = {
  whatsapp: { icon: "text-emerald-500", bg: "bg-emerald-500/10" },
  slack: { icon: "text-violet-500", bg: "bg-violet-500/10" },
};

export default function ChannelIntegrationCards({
  integrations = DEFAULT_INTEGRATIONS,
  onConfigure,
}: ChannelIntegrationCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {integrations.map((integration) => {
        const Icon = integration.icon;
        const isConfigured = (integration.count ?? 0) > 0;
        const color = INTEGRATION_COLORS[integration.type] || { icon: "text-primary", bg: "bg-primary/10" };
        return (
          <button
            key={integration.type}
            type="button"
            onClick={() => onConfigure(integration.type)}
            className="group flex flex-col items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-md"
          >
            <div className="flex w-full items-start justify-between">
              <div className={cn("flex h-11 w-11 items-center justify-center rounded-2xl", color.bg)}>
                <Icon className={cn("h-5 w-5", color.icon)} />
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground/50 transition-colors group-hover:text-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{integration.name}</h3>
                {integration.count !== undefined && (
                  <Badge
                    variant={isConfigured ? "default" : "outline"}
                    className={cn("text-[11px]", !isConfigured && "text-muted-foreground")}
                  >
                    {isConfigured ? `${integration.count} configurado${integration.count !== 1 ? "s" : ""}` : "Configurar"}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{integration.description}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
